// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { Body, Column, Description, Toast, ToastContext } from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { saveOrderFailed, updateOrder } from '~shared/actions/checkout';
import { DEFAULT_ERROR_ALERT_TITLE } from '~shared/constants';
import { PAYMENT_ERRORS } from '~shared/features/payment-methods/constants';
import { STALE_FEES_ERROR_CODE } from '~shared/features/shipment-fees/constants';
import { useQueryShipmentFeesAvailable } from '~shared/queries';
import { queries } from '~shared/queries/query-keys';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { useDispatchShared } from '~shared/store';
import { type APIError } from '~shared/types';
import { useSendCreateOrderAnalyticsEvent } from './useSendCreateOrderAnalyticsEvent';

export const useHandleCreateOrderError = () => {
  const { addToast } = useContext(ToastContext);
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const { sendCreateOrderAnalyticsEvent } = useSendCreateOrderAnalyticsEvent();
  const { invalidateShipmentFeesAvailable } = useQueryShipmentFeesAvailable();
  const { refetch: refetchPaymentMethods } = useGetPaymentMethods({ enabled: false });

  const handleCreateOrderError = (error: APIError) => {
    if (error) {
      const isPaymentPreAuthError = error.details?.code === PAYMENT_ERRORS.PREAUTH_FAILED_ERROR;
      dispatch(saveOrderFailed(error));
      sendCreateOrderAnalyticsEvent({ error });
      // we are fetching payment methods here after a save order failure to get the most up-to-date payment method data
      // the payment method data includes the field most_recent_charge_status which we'll use for error highlighting on the payment methods
      if (isPaymentPreAuthError) {
        refetchPaymentMethods();
      }

      // refetch order pricing and available fees and clear date/window selection if fees aren't aligned with BE
      if (error.details?.code === STALE_FEES_ERROR_CODE) {
        queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        invalidateShipmentFeesAvailable();
        dispatch(updateOrder({ date: undefined, deliver_before: undefined, deliver_after: undefined }));
      }

      // refetch the cart from the server, since it may have changed even if order creation failed
      queryClient.invalidateQueries({ queryKey: queries.cart.active({}).queryKey });

      const errorMessage = error.message ?? DEFAULT_ERROR_ALERT_TITLE;
      const title = isPaymentPreAuthError ? 'Payment failed' : errorMessage;
      const detailsMessage = error.details?.message;
      // don't show the detailed message if it's the same as the error message
      const message = errorMessage === detailsMessage ? undefined : detailsMessage;

      addToast(
        <Toast
          duration={8000}
          variant="error"
          dismissible
        >
          <Column gap="SM">
            <Body color={COLORS.TEXT_COLORS.WHITE}>{title}</Body>
            {message ? <Description color={COLORS.TEXT_COLORS.WHITE}>{message}</Description> : null}
          </Column>
        </Toast>,
      );
    }
  };

  return { handleCreateOrderError };
};
